import { isAdmin } from '@resistapp/common/features';
import { User } from '@resistapp/common/types';
import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useLocalStorageState from 'use-local-storage-state';
import { LocalStorageId, storedToken } from '../components/shared/local-storage';
import { useNavigateWithQuery } from '../hooks/use-navigate-with-query';
import { getLoginPathForLocation } from '../utils/login-return-path';
import { useGetUser, usePostLogin } from '../hooks/api';

interface UserContextType {
  user: User | undefined | null;
  logout: (url?: string | number) => void;
  loginErrorMessage: string | undefined;
  userLoading: boolean;
  login: (email: string, password: string) => void;
  loggingIn: boolean;
  setLoggedIn: (userData: User) => void;
  isAdmin: boolean;
}

const UserContext = React.createContext<UserContextType>({
  user: undefined,
  logout: () => {},
  loginErrorMessage: undefined,
  userLoading: false,
  login: () => {},
  loggingIn: false,
  setLoggedIn: () => {},
  isAdmin: false,
});

export function useUser() {
  return useContext(UserContext);
}

export function UserContextProvider(props: { children: ReactNode }) {
  const navigate = useNavigateWithQuery();
  const queryClient = useQueryClient();
  const { data, loading: userLoading, send: getUser } = useGetUser();
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | undefined>();
  const postLogin = usePostLogin(
    userData => {
      setLoggedIn(userData);
    },
    localError => {
      setLoginErrorMessage(localError.message);
      setLoggingIn(false);
    },
  );
  // User is logged in fully, when user is set. When the user refreshes the page we retrieve the user data through API
  // and set the user data again.
  const [user, setUser] = useState<User | undefined | null>(undefined);
  const [loggingIn, setLoggingIn] = useState(false);
  const [teamBrowser, setTeamBrowser] = useLocalStorageState<boolean>(LocalStorageId.teamBrowser, {
    defaultValue: false,
  });
  const [, storeToken, { removeItem: removeToken }] = useLocalStorageState<string>(LocalStorageId.token, {
    defaultValue: undefined,
  });
  const token = storedToken();
  const setLoggedIn = (userData: User) => {
    userData.token && storeToken(userData.token);
    if (isAdmin(userData) && !teamBrowser) {
      setTeamBrowser(true); // Never unset
    }
    setUser(userData);
    setLoggingIn(false);
  };

  useEffect(() => {
    // Only hydrate from /user when we still have a token and we haven't explicitly logged out.
    // This avoids stale in-flight /user responses repopulating user after logout.
    if (!data || !token || user !== undefined) {
      return;
    }
    setUser(data);
  }, [data, token, user]);

  useEffect(() => {
    if (!token) {
      setUser(null);
      return;
    }
    if (user !== undefined) {
      return;
    }
    let mutatingIsRequestActive = true; // Avoid applying stale /user responses after token changes/logout.
    const requestToken = token;
    void getUser()
      .then(response => {
        if (!mutatingIsRequestActive || storedToken() !== requestToken) {
          return;
        }
        if (response.status === 401) {
          removeToken();
          setUser(null);
          queryClient.clear();
          const loginPath = typeof window === 'undefined' ? '/login' : getLoginPathForLocation(window.location);
          navigate(loginPath, false, true);
          return;
        }

        const isSuccessfulStatus = typeof response.status === 'number' && response.status >= 200 && response.status <= 204;
        if (isSuccessfulStatus && response.data) {
          setUser(response.data as User);
          return;
        }

        // Resolve auth state even when /user fails (e.g. 5xx/network issues)
        // so guarded routes do not stay in an indefinite loading state.
        setUser(null);
      })
      .catch(() => {
        if (!mutatingIsRequestActive || storedToken() !== requestToken) {
          return;
        }
        setUser(null);
      });
    return () => {
      mutatingIsRequestActive = false;
    };
  }, [getUser, navigate, queryClient, removeToken, token, user]);

  const logout = useCallback(
    (url?: string | number) => {
      // Clear client state synchronously to avoid a race where the app redirects
      // to protected routes before the token is removed.
      setLoginErrorMessage(undefined);
      setLoggingIn(false);
      removeToken();
      setUser(null);
      queryClient.clear();

      if (typeof url === 'number') {
        navigate(url);
      } else {
        navigate(url ?? '/login', false, true);
      }

      if (typeof window !== 'undefined') {
        void fetch('/api/logout', { method: 'POST', credentials: 'include', keepalive: true }).catch(error => {
          console.warn('Failed to clear auth cookie', error);
        });
      }
    },
    [navigate, queryClient, removeToken],
  );
  const login = useCallback(
    (email: string, password: string) => {
      setLoggingIn(true);
      void postLogin(email, password);
    },
    [postLogin],
  );

  return (
    <UserContext.Provider
      value={{
        user,
        loggingIn,
        login,
        setLoggedIn,
        userLoading,
        logout,
        loginErrorMessage,
        isAdmin: isAdmin(user),
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
}
